
  import videojs from 'video.js';
  import hlsQualitySelector from 'videojs-hls-quality-selector';
  import qualityLevels from 'videojs-contrib-quality-levels';
  import { defineComponent } from 'vue';
  import props from './props';

  import type { Source } from '.';

  // Quality selector not working on Safari
  // Safari issue: https://github.com/chrisboustead/videojs-hls-quality-selector/issues/80
  if (!videojs.getPlugin('qualityLevels')) {
    videojs.registerPlugin('qualityLevels', qualityLevels);
  }

  if (!videojs.getPlugin('hlsQualitySelector')) {
    videojs.registerPlugin('hlsQualitySelector', hlsQualitySelector);
  }

  export default defineComponent({
    name: 'AppVideoPlayer',

    props,

    emits: [
      'ready',
      'play',
      'end',
      'pause',
      'seeking',
      'timeupdate',
      'error',
    ],

    data() {
      return {
        player: null as videojs.Player | null,
        sources: [] as Source[],
        time: 0,
        duration: 0,
      };
    },

    mounted() {
      const player = this.$refs.videoPlayer as Element;

      // Parse URL to check extension without query params (S3 signed URLs have query strings)
      const getUrlExtension = (url: string): string => {
        try {
          const pathname = new URL(url).pathname;
          return pathname.slice(pathname.lastIndexOf('.')).toLowerCase();
        } catch {
          // Fallback for relative URLs
          const pathPart = url.split('?')[0];
          return pathPart.slice(pathPart.lastIndexOf('.')).toLowerCase();
        }
      };

      const isMp4Source = this.playlist && getUrlExtension(this.playlist) === '.mp4';
      const isHlsSource = this.playlist && !isMp4Source;
      const playerOptions: videojs.PlayerOptions = {
        controls: this.controls,
        autoplay: this.autoplay,
        loop: this.loop,
        preload: this.preload,
        poster: this.poster,
        fluid: this.fluid,
        // For MP4 sources, disable VHS to prevent it from treating MP4 as HLS playlist.
        // This fixes slow loading in Chrome where VHS repeatedly tries to parse MP4 as m3u8.
        html5: {
          vhs: {
            overrideNative: isHlsSource,
          },
          nativeVideoTracks: !isHlsSource,
          nativeAudioTracks: !isHlsSource,
        },
        plugins: isHlsSource
          ? {
            hlsQualitySelector: {
              displayCurrentQuality: true,
            },
          }
          : {},
        ...this.options,
      };

      if (this.playlist) {
        this.sources = [
          {
            src: this.playlist,
            type: isMp4Source ? 'video/mp4' : 'application/x-mpegURL',
          },
        ];
      } else if (this.source) {
        this.sources = this.source;
      }

      this.$nextTick(() => {
        this.player = videojs(player, playerOptions, () => {
          if (this.autoplay) {
            this.player?.play();
          }

          this.ready();
        });
      });
    },

    beforeUnmount() {
      this.player?.dispose();
    },

    methods: {
      ready(): void {
        if (this.player) {
          this.player.on('play', () => {
            this.$emit('play', this.player);
          });

          this.player.on('ended', () => {
            this.$emit('end', this.player);
          });

          this.player.on('pause', () => {
            this.$emit('pause', this.player);
          });

          this.player.on('timeupdate', () => {
            const currentTime = this.player?.currentTime() ?? 0;

            this.time = Math.floor(currentTime);
            const percentProgress = Math.round((currentTime / this.duration) * 100);

            this.$emit('timeupdate', {
              time: this.time,
              percentage: percentProgress,
              duration: this.duration,
            });
          });

          this.player.on('error', () => {
            this.$emit('error', this.player);
          });

          this.player.on('seeking', () => {
            this.$emit('seeking', this.player);
          });

          this.player.on('loadedmetadata', () => {
            this.duration = this.player?.duration() ?? 0;

            this.$emit('ready', this.player);
          });
        }
      },

      play(fullscreen: false): void {
        if (fullscreen) {
          this.player?.requestFullscreen();
        }

        this.player?.play();
      },
    },
  });
